import React, { useState } from "react";
import styled from "styled-components";

export default function MarketingMediaSlot({
  src,
  alt,
  label,
  hint,
  aspect = "16 / 10",
  className,
  objectPosition = "center",
  objectFit = "cover",
  objectScale = 1,
  ...frameProps
}) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  return (
    <Frame className={className} $aspect={aspect} {...frameProps}>
      <Placeholder aria-hidden={showImage} $hidden={showImage}>
        <Glow />
        <Grid />
        <Line />
        <Panel />
        <Meta>
          {label ? <Badge>{label}</Badge> : null}
          {hint ? <Hint>{hint}</Hint> : null}
        </Meta>
      </Placeholder>
      {showImage ? (
        <Image
          src={src}
          alt={alt || ""}
          $objectPosition={objectPosition}
          $objectFit={objectFit}
          $objectScale={objectScale}
          onError={() => setFailed(true)}
        />
      ) : null}
      <Overlay />
    </Frame>
  );
}

const Frame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${(p) => p.$aspect};
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    radial-gradient(
      120% 140% at 0% 0%,
      rgba(255, 255, 255, 0.08),
      rgba(255, 255, 255, 0) 38%
    ),
    radial-gradient(
      160% 140% at 78% 18%,
      rgba(160, 160, 160, 0.14),
      rgba(12, 12, 12, 0.88) 46%
    ),
    #080808;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 26px 80px rgba(0, 0, 0, 0.28);
`;

const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
`;

const Glow = styled.div`
  position: absolute;
  inset: -12% -8% auto auto;
  width: 64%;
  height: 76%;
  background: radial-gradient(
    circle at 40% 40%,
    rgba(255, 255, 255, 0.18),
    rgba(255, 255, 255, 0.04) 34%,
      rgba(255, 255, 255, 0) 66%
  );
  filter: blur(18px);
  opacity: 0.52;
`;

const Line = styled.div`
  position: absolute;
  top: 18%;
  right: 12%;
  width: 46%;
  height: 1px;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0));
  opacity: 0.6;
`;

const Panel = styled.div`
  position: absolute;
  right: 16%;
  bottom: 17%;
  width: 42%;
  height: 30%;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 18px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02)),
    rgba(255, 255, 255, 0.02);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 18px 42px rgba(0, 0, 0, 0.28);
  transform: perspective(900px) rotateX(58deg) rotateY(-18deg);
  transform-origin: center;
  opacity: 0.42;
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.05),
      rgba(255, 255, 255, 0.015)
    ),
    repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.03) 0,
      rgba(255, 255, 255, 0.03) 1px,
      transparent 1px,
      transparent 40px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.03) 0,
      rgba(255, 255, 255, 0.03) 1px,
      transparent 1px,
      transparent 40px
    );
  opacity: 0.22;
`;

const Meta = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 10px;
  padding: 22px;
  max-width: 300px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(8, 8, 8, 0.45);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Hint = styled.span`
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.74);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
`;

const Image = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: ${(p) => p.$objectFit};
  object-position: ${(p) => p.$objectPosition};
  transform: scale(${(p) => p.$objectScale});
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(
      180deg,
      rgba(6, 6, 6, 0.02) 0%,
      rgba(6, 6, 6, 0.12) 46%,
      rgba(6, 6, 6, 0.42) 100%
    );
`;
